import React, { useState } from 'react';
import { Sparkles, Send, Check, Loader2, Phone, Mail, X } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';

const PerfumeSuggestionModal = ({ isOpen, onClose }) => {
  const { API, t, language } = useApp();
  const [formData, setFormData] = useState({
    perfume_name: '',
    brand: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    return phoneRegex.test(phone.replace(/[\s-]/g, ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.perfume_name.trim()) {
      toast.error(t('perfume_name_required'));
      return;
    }
    
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error(t('enter_valid_email'));
      return;
    }
    
    if (!formData.phone || !validatePhone(formData.phone)) {
      toast.error(t('valid_phone_required'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/suggestions/perfume`, {
        ...formData,
        language: language
      });
      
      toast.success(t('suggestion_submitted'));
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('suggestion_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      perfume_name: '',
      brand: '',
      email: '',
      phone: '',
      notes: ''
    });
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative glass rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="perfume-suggestion-modal">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-[#A8A29E] hover:text-white transition-colors"
          data-testid="suggestion-modal-close"
        >
          <X size={24} />
        </button>

        {submitted ? (
          <div className="flex flex-col items-center justify-center gap-4 text-center py-8">
            <div className="p-4 bg-green-500/10 rounded-full">
              <Check size={32} className="text-green-500" />
            </div>
            <h3 className="font-heading text-xl text-white">{t('suggestion_received')}</h3>
            <p className="text-[#A8A29E] text-sm max-w-md">
              {t('suggestion_confirmation_message')}
            </p>
            <button
              onClick={handleClose}
              className="btn-primary mt-4"
            >
              {t('close')}
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF37]/10 rounded-full">
                <Sparkles size={24} className="text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-heading text-lg text-white">{t('suggest_perfume')}</h3>
                <p className="text-[#A8A29E] text-sm">{t('suggest_perfume_desc')}</p>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Perfume Name - Required */}
              <div>
                <label className="block text-[#A8A29E] text-sm mb-1">
                  {t('perfume_name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="perfume_name"
                  value={formData.perfume_name}
                  onChange={handleChange}
                  placeholder={t('perfume_name_placeholder')}
                  className="input-luxury w-full"
                  disabled={loading}
                  data-testid="suggestion-perfume-name"
                />
              </div>

              {/* Brand - Optional */}
              <div>
                <label className="block text-[#A8A29E] text-sm mb-1">
                  {t('brand')} ({t('optional')})
                </label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  placeholder={t('brand_placeholder')}
                  className="input-luxury w-full"
                  disabled={loading}
                  data-testid="suggestion-brand"
                />
              </div>

              {/* Email - Required */}
              <div>
                <label className="block text-[#A8A29E] text-sm mb-1">
                  {t('email')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('your_email')}
                    className="input-luxury w-full pl-10"
                    disabled={loading}
                    data-testid="suggestion-email"
                  />
                </div>
              </div>

              {/* Phone - Required */}
              <div>
                <label className="block text-[#A8A29E] text-sm mb-1">
                  {t('phone')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('phone_placeholder')}
                    className="input-luxury w-full pl-10"
                    disabled={loading}
                    data-testid="suggestion-phone"
                  />
                </div>
                <p className="text-[#57534E] text-xs mt-1">{t('phone_whatsapp_note')}</p>
              </div>

              {/* Additional Notes - Optional */}
              <div>
                <label className="block text-[#A8A29E] text-sm mb-1">
                  {t('additional_notes')} ({t('optional')})
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder={t('suggestion_notes_placeholder')}
                  rows={3}
                  className="input-luxury w-full resize-none"
                  disabled={loading}
                  data-testid="suggestion-notes"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="suggestion-submit-btn"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    <Send size={18} />
                    {t('submit_suggestion')}
                  </>
                )}
              </button>
            </form>
            
            <p className="text-[#57534E] text-xs mt-4 text-center">
              {t('suggestion_privacy_note')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// Footer button component that triggers the modal
const PerfumeSuggestionButton = ({ onClick }) => {
  const { t } = useApp();
  
  return (
    <button
      onClick={onClick}
      className="btn-primary flex items-center justify-center gap-2 px-6"
      data-testid="perfume-suggestion-btn"
    >
      <Sparkles size={18} />
      {t('suggest_perfume_btn')}
    </button>
  );
};

export { PerfumeSuggestionModal, PerfumeSuggestionButton };
export default PerfumeSuggestionModal;
